// Real backend API client (replaces the previous localStorage-only mock)
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const KEY_ADMIN_TOKEN = 'fbx_admin_token';
const KEY_CLIENT_TOKEN = 'fbx_client_token_'; // suffixed with clientSlug
const KEY_CLIENT_NAME = 'fbx_client_name_';   // suffixed with clientSlug

// axios instances
const http = axios.create({ baseURL: API_BASE });

http.interceptors.request.use((config) => {
  // Try admin token first for admin routes
  const url = config.url || '';
  if (url.startsWith('/admin/')) {
    const t = localStorage.getItem(KEY_ADMIN_TOKEN);
    if (t) config.headers.Authorization = `Bearer ${t}`;
  } else if (url.startsWith('/client/')) {
    // Client routes need client token — expect caller to have set X-Client-Slug on config.meta or globally.
    const slug = config.__clientSlug;
    if (slug) {
      const t = localStorage.getItem(KEY_CLIENT_TOKEN + slug);
      if (t) config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// ---------------- Admin ----------------
export const adminApi = {
  async login({ email, password }) {
    const { data } = await http.post('/admin/login', { email, password });
    localStorage.setItem(KEY_ADMIN_TOKEN, data.token);
    return data.admin;
  },
  logout() { localStorage.removeItem(KEY_ADMIN_TOKEN); },
  hasToken() { return !!localStorage.getItem(KEY_ADMIN_TOKEN); },
  async me() {
    const { data } = await http.get('/admin/me');
    return data;
  },

  // Clients
  async listClients() {
    const { data } = await http.get('/admin/clients');
    return data;
  },
  async createClient(payload) {
    const { data } = await http.post('/admin/clients', payload);
    return data;
  },
  async getClient(slug) {
    const { data } = await http.get(`/admin/clients/${slug}`);
    return data;
  },
  async updateClient(id, patch) {
    const { data } = await http.patch(`/admin/clients/${id}`, patch);
    return data;
  },
  async deleteClient(id) {
    await http.delete(`/admin/clients/${id}`);
  },

  // Galleries
  async createGallery(clientId, payload) {
    const { data } = await http.post(`/admin/clients/${clientId}/galleries`, payload);
    return data;
  },
  async listSubgalleries(galleryId) {
    const { data } = await http.get(`/admin/galleries/${galleryId}/subgalleries`);
    return data;
  },
  async createSubgallery(galleryId, payload) {
    const { data } = await http.post(`/admin/galleries/${galleryId}/subgalleries`, payload);
    return data;
  },
  async updateSubgallery(subgalleryId, patch) {
    const { data } = await http.patch(`/admin/subgalleries/${subgalleryId}`, patch);
    return data;
  },
  async updateGallery(galleryId, patch) {
    const { data } = await http.patch(`/admin/galleries/${galleryId}`, patch);
    return data;
  },
  async deleteSubgallery(subgalleryId) {
    await http.delete(`/admin/subgalleries/${subgalleryId}`);
  },
  async addPhoto(subgalleryId, photo) {
    const { data } = await http.post(`/admin/subgalleries/${subgalleryId}/photos`, photo);
    return data;
  },
  async getSubgallery(subgalleryId) {
    const { data } = await http.get(`/admin/subgalleries/${subgalleryId}`);
    return data;
  },
  async deletePhoto(photoId) {
    await http.delete(`/admin/photos/${photoId}`);
  },
  async setSubgalleryCover(subgalleryId, photoId) {
    const { data } = await http.post(`/admin/subgalleries/${subgalleryId}/cover`, { photo_id: photoId });
    return data;
  },
  async setGalleryCover(galleryId, photoId) {
    const { data } = await http.post(`/admin/galleries/${galleryId}/cover`, { photo_id: photoId });
    return data;
  },
  async setClientCover(clientId, photoId) {
    const { data } = await http.post(`/admin/clients/${clientId}/cover`, { photo_id: photoId });
    return data;
  },
  async sendClientCredentials(clientId, { channels, password }) {
    const { data } = await http.post(`/admin/clients/${clientId}/send-credentials`, { channels, password });
    return data;
  },
  async reorderSubgalleryPhotos(subgalleryId, photoIds) {
    const { data } = await http.post(`/admin/subgalleries/${subgalleryId}/reorder`, { photo_ids: photoIds });
    return data;
  },
  async deleteGallery(galleryId) {
    await http.delete(`/admin/galleries/${galleryId}`);
  },
  async galleryStats(galleryId) {
    const { data } = await http.get(`/admin/galleries/${galleryId}/stats`);
    return data;
  },

  // Notifications
  async listNotifications() {
    const { data } = await http.get('/admin/notifications');
    return data;
  },
  async markNotificationRead(id) {
    await http.post(`/admin/notifications/${id}/read`);
  },
  async deleteNotification(id) {
    await http.delete(`/admin/notifications/${id}`);
  },

  // Selections
  async listSelections() {
    const { data } = await http.get('/admin/selections');
    return data;
  },
};

// ---------------- Client (public) ----------------
export const clientApi = {
  async getInfo(slug) {
    const { data } = await http.get(`/public/clients/${slug}/info`);
    return data;
  },
  async unlockGlobal({ password, display_name }) {
    const { data } = await http.post('/public/unlock', { password, display_name });
    localStorage.setItem(KEY_CLIENT_TOKEN + data.slug, data.token);
    localStorage.setItem(KEY_CLIENT_NAME + data.slug, display_name);
    return data;
  },
  async unlock(slug, { password, display_name }) {
    const { data } = await http.post(`/public/clients/${slug}/unlock`, { password, display_name });
    localStorage.setItem(KEY_CLIENT_TOKEN + slug, data.token);
    localStorage.setItem(KEY_CLIENT_NAME + slug, display_name);
    return data;
  },
  hasToken(slug) { return !!localStorage.getItem(KEY_CLIENT_TOKEN + slug); },
  displayName(slug) { return localStorage.getItem(KEY_CLIENT_NAME + slug) || ''; },
  logout(slug) {
    localStorage.removeItem(KEY_CLIENT_TOKEN + slug);
    localStorage.removeItem(KEY_CLIENT_NAME + slug);
  },
  async me(slug) {
    const { data } = await http.get('/client/me', { __clientSlug: slug });
    return data;
  },
  async getGallery(slug, gallerySlug) {
    const { data } = await http.get(`/client/galleries/${gallerySlug}`, { __clientSlug: slug });
    return data;
  },
  async getSubgallery(slug, gallerySlug, subgallerySlug) {
    const { data } = await http.get(`/client/galleries/${gallerySlug}/subgalleries/${subgallerySlug}`, { __clientSlug: slug });
    return data;
  },
  async toggleLike(slug, photoId) {
    const { data } = await http.post(`/client/photos/${photoId}/like`, {}, { __clientSlug: slug });
    return data;
  },
  async addComment(slug, photoId, text) {
    const { data } = await http.post(`/client/photos/${photoId}/comments`, { text }, { __clientSlug: slug });
    return data;
  },
  async submitSelection(slug, { subgalleryId, photoIds, note }) {
    const { data } = await http.post('/client/selections', { subgallery_id: subgalleryId, photo_ids: photoIds, note }, { __clientSlug: slug });
    return data;
  },
};

// Legacy compat: some components import PHOTOS from mock for the "add sample photos" button
export { PHOTOS } from './mock';
